import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useResponsibilityStore } from '../../../stores/prioritization/responsibilityStore';
import { C } from '../theme/participantTokens';
import { useBatchSubmissionStats } from '../hooks/useMySubmissionStatus';
import { useParticipantSessions } from '../hooks/useParticipantSessions';
import { usePublishedPlans } from '../hooks/usePublishedPlans';
import type { DashboardFilter } from '../utils/sessionHelpers';
import { firstName, formatShortDate, greeting, isValueSession } from '../utils/sessionHelpers';
import { getValueSessionGapStats } from '../utils/valueSessionGaps';
import { ballSummaryLine, selectExchangeCards } from '../utils/exchangeCards';
import { LayerBar } from './layout/ParticipantLayout';
import { SectionEyebrow } from './layout/ParticipantLayout';
import { ExchangeCard } from './ExchangeCard';
import { PlanPublishedCard } from './PlanPublishedCard';
import { SessionCard } from './SessionCard';

const FILTER_LABELS: Record<DashboardFilter, string> = {
  all: 'All Sessions',
  needs_input: 'Needs Input',
  live: 'Live Now',
  upcoming: 'Upcoming',
  completed: 'Completed',
};

interface SessionDashboardProps {
  onOpenSession: (sessionId: string) => void;
}

export function SessionDashboard({ onOpenSession }: SessionDashboardProps) {
  const navigate = useNavigate();
  const { userId, userName, groups, needsInputCount, filterGroups, mySessions } = useParticipantSessions();
  const [filter, setFilter] = useState<DashboardFilter>('all');
  const filtered = filterGroups(filter);
  const allForStats = useMemo(
    () => [...groups.needsInput, ...groups.live, ...groups.upcoming, ...groups.completed],
    [groups],
  );
  const statsMap = useBatchSubmissionStats(allForStats, userId);
  const fetchValueState = useValueProposalStore((s) => s.fetchFromSupabase);
  const exceptions = useValueProposalStore((s) => s.exceptions);
  const recomputeAllEligibility = useSessionStore((s) => s.recomputeAllEligibility);

  // The exchange side of the stack — the Next-Step Engine's server-authoritative
  // output (Doc 09), selected and ordered for display only (Doc 06).
  const responsibilities = useResponsibilityStore((s) => s.responsibilities);
  const fetchResponsibilities = useResponsibilityStore((s) => s.fetchResponsibilities);
  useEffect(() => {
    if (userId) void fetchResponsibilities(userId);
  }, [userId, fetchResponsibilities]);
  const exchange = useMemo(() => selectExchangeCards(responsibilities), [responsibilities]);

  // The closing loop: cycles this viewer ranked in whose plan is now published.
  const publishedPlans = usePublishedPlans(mySessions);

  // GAP-5: hydrate exceptions + refresh eligibility so value cards show real gap counts
  useEffect(() => {
    void fetchValueState();
  }, [fetchValueState]);

  const valueSessionIdsKey = mySessions
    .filter(isValueSession)
    .map((s) => s.id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!valueSessionIdsKey) return;
    for (const id of valueSessionIdsKey.split(',')) {
      recomputeAllEligibility(id);
    }
  }, [valueSessionIdsKey, recomputeAllEligibility]);

  const layerBtnStyle = (active: boolean) => ({
    height: '100%',
    padding: '0 16px',
    background: active ? C.activeTint : 'none',
    border: 'none',
    borderRight: `1px solid ${C.rule}`,
    cursor: 'pointer',
    fontFamily: 'JetBrains Mono',
    fontSize: '0.6rem',
    letterSpacing: '0.07em',
    color: active ? C.textPri : C.textTer,
    transition: 'background 0.15s, color 0.15s',
    whiteSpace: 'nowrap' as const,
  });

  const needsYourInput = [...filtered.live, ...filtered.needsInput];
  // Exchange and plan cards belong to the unfiltered stack — the session
  // filters stay honest about sessions only.
  const showExchange = filter === 'all';
  const requiredExchange = showExchange ? exchange.required : [];
  const waitingExchange = showExchange ? exchange.waiting : [];
  const plans = showExchange ? publishedPlans : [];
  const hasAny =
    needsYourInput.length > 0
    || filtered.upcoming.length > 0
    || filtered.completed.length > 0
    || requiredExchange.length > 0
    || waitingExchange.length > 0
    || plans.length > 0;

  return (
    <>
      <LayerBar>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.55rem', color: C.textTer, letterSpacing: '0.16em', textTransform: 'uppercase', padding: '0 16px', borderRight: `1px solid ${C.rule}`, height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Filter
        </div>
        {(Object.keys(FILTER_LABELS) as DashboardFilter[]).map(f => (
          <button key={f} type="button" style={layerBtnStyle(filter === f)} onClick={() => setFilter(f)}>
            {FILTER_LABELS[f]}
          </button>
        ))}
      </LayerBar>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '28px 32px 48px', maxWidth: 860 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase', color: C.textDim, marginBottom: 4 }}>
            Your workspace
          </div>
          <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.6rem', fontWeight: 600, color: C.textPri, letterSpacing: '0.01em', lineHeight: 1.2, marginBottom: 6 }}>
            Good {greeting()}, {firstName(userName)}.
          </div>
          <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, lineHeight: 1.5, marginBottom: 24 }}>
            {/* The ball is always named (Doc 26 rule 4) */}
            {ballSummaryLine(needsInputCount + exchange.required.length, exchange.waiting.length)}
            {groups.live.length > 0 && ` ${groups.live.length} session${groups.live.length === 1 ? ' is' : 's are'} live right now.`}
          </div>

          {(needsYourInput.length > 0 || requiredExchange.length > 0) && (
            <>
              <SectionEyebrow>Needs Your Input</SectionEyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {requiredExchange.map(card => (
                  <ExchangeCard key={card.id} card={card} />
                ))}
                {needsYourInput.map(s => {
                  const gaps = isValueSession(s) ? getValueSessionGapStats(s, exceptions) : null;
                  const dueLabel = gaps?.dueDate ? formatShortDate(gaps.dueDate) : undefined;
                  return (
                    <SessionCard
                      key={s.id}
                      session={s}
                      stats={statsMap[s.id]}
                      dueLabel={dueLabel}
                      onClick={() => onOpenSession(s.id)}
                    />
                  );
                })}
              </div>
            </>
          )}

          {waitingExchange.length > 0 && (
            <>
              {(needsYourInput.length > 0 || requiredExchange.length > 0) && (
                <hr style={{ border: 'none', borderTop: `1px solid ${C.rule}`, margin: '24px 0 18px' }} />
              )}
              <SectionEyebrow>Waiting On Others</SectionEyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {waitingExchange.map(card => (
                  <ExchangeCard key={card.id} card={card} />
                ))}
              </div>
            </>
          )}

          {filtered.upcoming.length > 0 && (
            <>
              {(needsYourInput.length > 0 || requiredExchange.length > 0 || waitingExchange.length > 0) && (
                <hr style={{ border: 'none', borderTop: `1px solid ${C.rule}`, margin: '24px 0 18px' }} />
              )}
              <SectionEyebrow>Upcoming</SectionEyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {filtered.upcoming.map(s => (
                  <SessionCard key={s.id} session={s} stats={statsMap[s.id]} onClick={() => onOpenSession(s.id)} />
                ))}
              </div>
            </>
          )}

          {(filtered.completed.length > 0 || plans.length > 0) && (
            <>
              {(needsYourInput.length > 0 || requiredExchange.length > 0 || waitingExchange.length > 0 || filtered.upcoming.length > 0) && (
                <hr style={{ border: 'none', borderTop: `1px solid ${C.rule}`, margin: '24px 0 18px' }} />
              )}
              <SectionEyebrow>Recently Completed</SectionEyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {plans.map(p => (
                  <PlanPublishedCard key={p.report.id} plan={p} />
                ))}
                {filtered.completed.map(s => (
                  <SessionCard key={s.id} session={s} stats={statsMap[s.id]} onClick={() => onOpenSession(s.id)} />
                ))}
              </div>
            </>
          )}

          {!hasAny && (
            /* Three-card entry (brief-X §4.3): the empty stack offers the first
               valid actions instead of a dead end. */
            <div>
              <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, marginBottom: 16 }}>
                Nothing needs you yet. Where would you like to start?
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                {[
                  {
                    title: 'Quick decision',
                    body: 'Just you. Rank a handful of options in minutes — no setup needed.',
                    to: '/prioritization/sessions/new',
                  },
                  {
                    title: 'Team session',
                    body: 'Invite people, score together live, leave with a held result.',
                    to: '/prioritization/sessions/new',
                  },
                  {
                    title: 'Full cycle',
                    body: 'Enterprise-grade: exchanges up and down, ending in published priorities.',
                    to: '/prioritization/cycles',
                  },
                ].map(entry => (
                  <div
                    key={entry.title}
                    className="pp-anim pp-session-card"
                    onClick={() => navigate(entry.to)}
                    style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '18px 16px', cursor: 'pointer' }}
                  >
                    <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1rem', fontWeight: 400, color: C.textPri, marginBottom: 6 }}>
                      {entry.title}
                    </div>
                    <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.8rem', fontWeight: 300, color: C.textTer, lineHeight: 1.4, marginBottom: 12 }}>
                      {entry.body}
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: C.mustard }}>
                      Start →
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
